import { TableConfig, Student } from '../types';
import { STUDENTS } from '../data/students';

// 테이블 구성
export const TABLES: TableConfig[] = [
  { tableNumber: 1, capacity: 7, currentOccupancy: 0, type: '7인용' },
  { tableNumber: 2, capacity: 7, currentOccupancy: 0, type: '7인용' },
  { tableNumber: 3, capacity: 7, currentOccupancy: 0, type: '7인용' },
  { tableNumber: 4, capacity: 8, currentOccupancy: 0, type: '8인용' },
  { tableNumber: 5, capacity: 8, currentOccupancy: 0, type: '8인용' },
  { tableNumber: 6, capacity: 7, currentOccupancy: 0, type: '7인용' },
  { tableNumber: 7, capacity: 8, currentOccupancy: 0, type: '8인용' },
];

// 테이블 용량 업데이트 (6-8명 제한)
export const updateTableCapacity = (tableNumber: number, newCapacity: number): void => {
  const table = TABLES.find(t => t.tableNumber === tableNumber);
  if (table) {
    // 6-8명 제한 적용
    const limitedCapacity = Math.max(6, Math.min(8, newCapacity));
    table.capacity = limitedCapacity;
    table.type = `${limitedCapacity}인용`;
    
    // 현재 점유율이 새로운 용량을 초과하면 조정
    if (table.currentOccupancy > limitedCapacity) {
      table.currentOccupancy = limitedCapacity;
    }
  }
};

// 테이블 설정 저장
export const saveTableSettings = (): void => {
  try {
    localStorage.setItem('table-settings', JSON.stringify(TABLES));
  } catch (error) {
    console.error('테이블 설정 저장 실패:', error);
  }
};

// 테이블 설정 로드
export const loadTableSettings = (): void => {
  try {
    const saved = localStorage.getItem('table-settings');
    if (saved) {
      const savedTables = JSON.parse(saved);
      savedTables.forEach((savedTable: TableConfig) => {
        const table = TABLES.find(t => t.tableNumber === savedTable.tableNumber);
        if (table) {
          table.capacity = savedTable.capacity;
          table.type = savedTable.type;
          table.currentOccupancy = Math.min(savedTable.currentOccupancy, savedTable.capacity);
        }
      });
    }
  } catch (error) {
    console.error('테이블 설정 로드 실패:', error);
  }
};

// 랜덤 테이블 배정 (골고루 배정 개선)
export const assignRandomTable = (excludeFullTables: boolean = true): number => {
  const availableTables = TABLES.filter(table => 
    !excludeFullTables || table.currentOccupancy < table.capacity
  );
  
  if (availableTables.length === 0) {
    throw new Error('모든 테이블이 만석입니다.');
  }
  
  // 골고루 배정을 위한 가중치 계산
  const weightedTables = availableTables.map(table => {
    // 빈 자리가 많을수록 높은 가중치 (제곱으로 가중치 증가)
    const availableSeats = table.capacity - table.currentOccupancy;
    const weight = Math.pow(availableSeats, 2);
    
    return {
      ...table,
      weight,
      availableSeats
    };
  });
  
  // 총 가중치 계산
  const totalWeight = weightedTables.reduce((sum, table) => sum + table.weight, 0);
  
  // 가중치 기반 랜덤 선택
  let randomValue = Math.random() * totalWeight;
  
  for (const table of weightedTables) {
    randomValue -= table.weight;
    if (randomValue <= 0) {
      return table.tableNumber;
    }
  }
  
  // 폴백: 마지막 테이블 반환
  return weightedTables[weightedTables.length - 1].tableNumber;
};

// 균형 잡힌 테이블 배정 (가장 빈 자리가 많은 테이블 우선)
export const assignBalancedTable = (excludeFullTables: boolean = true): number => {
  const availableTables = TABLES.filter(table => 
    !excludeFullTables || table.currentOccupancy < table.capacity
  );
  
  if (availableTables.length === 0) {
    throw new Error('모든 테이블이 만석입니다.');
  }
  
  // 빈 자리가 가장 많은 테이블 찾기
  const bestTable = availableTables.reduce((best, current) => {
    const bestAvailable = best.capacity - best.currentOccupancy;
    const currentAvailable = current.capacity - current.currentOccupancy;
    
    if (currentAvailable > bestAvailable) {
      return current;
    }
    // 빈 자리가 같다면 더 작은 테이블 번호 선택
    if (currentAvailable === bestAvailable && current.tableNumber < best.tableNumber) {
      return current;
    }
    return best;
  });
  
  return bestTable.tableNumber;
};

// 순환 테이블 배정 (테이블 번호 순서대로, 만석인 테이블은 건너뛰기)
export const assignRoundRobinTable = (excludeFullTables: boolean = true): number => {
  const availableTables = TABLES.filter(table => 
    !excludeFullTables || table.currentOccupancy < table.capacity
  );
  
  if (availableTables.length === 0) {
    throw new Error('모든 테이블이 만석입니다.');
  }
  
  // 현재 가장 적은 사람이 있는 테이블 찾기
  const leastOccupiedTable = availableTables.reduce((least, current) => {
    if (current.currentOccupancy < least.currentOccupancy) {
      return current;
    }
    // 점유율이 같다면 더 작은 테이블 번호 선택
    if (current.currentOccupancy === least.currentOccupancy && current.tableNumber < least.tableNumber) {
      return current;
    }
    return least;
  });
  
  return leastOccupiedTable.tableNumber;
};

// 테이블 점유율 업데이트
export const updateTableOccupancy = (tableNumber: number, increment: boolean = true): void => {
  const table = TABLES.find(t => t.tableNumber === tableNumber);
  if (table) {
    const oldOccupancy = table.currentOccupancy;
    
    if (increment) {
      if (table.currentOccupancy < table.capacity) {
        table.currentOccupancy++;
        console.log(`테이블 ${tableNumber} 점유율 증가: ${oldOccupancy} → ${table.currentOccupancy}`);
      } else {
        console.warn(`테이블 ${tableNumber}가 이미 만석입니다.`);
      }
    } else {
      if (table.currentOccupancy > 0) {
        table.currentOccupancy--;
        console.log(`테이블 ${tableNumber} 점유율 감소: ${oldOccupancy} → ${table.currentOccupancy}`);
      } else {
        console.warn(`테이블 ${tableNumber}가 이미 비어있습니다.`);
      }
    }
  } else {
    console.error(`테이블 ${tableNumber}를 찾을 수 없습니다.`);
  }
};

// 테이블 현황 가져오기
export const getTableStatus = (): TableConfig[] => {
  return [...TABLES];
};

// 총 잔여석 계산
export const getTotalAvailableSeats = (): number => {
  return TABLES.reduce((total, table) => {
    return total + (table.capacity - table.currentOccupancy);
  }, 0);
};

// 테이블 점유율 시각화
export const getTableVisualization = (table: TableConfig): string => {
  const occupied = '■'.repeat(table.currentOccupancy);
  const empty = '□'.repeat(table.capacity - table.currentOccupancy);
  return occupied + empty;
};

// 학생을 테이블에 배정
export const assignStudentToTable = (student: Student, tableNumber: number): boolean => {
  const table = TABLES.find(t => t.tableNumber === tableNumber);
  if (!table || table.currentOccupancy >= table.capacity) {
    return false;
  }
  
  table.currentOccupancy++;
  return true;
};

// 테이블에서 학생 제거
export const removeStudentFromTable = (tableNumber: number): boolean => {
  const table = TABLES.find(t => t.tableNumber === tableNumber);
  if (!table || table.currentOccupancy <= 0) {
    return false;
  }
  
  table.currentOccupancy--;
  return true;
};

// 모든 테이블 초기화
export const resetAllTables = (): void => {
  TABLES.forEach(table => {
    table.currentOccupancy = 0;
  });
  console.log('모든 테이블 점유율이 초기화되었습니다.');
};

// 테이블 설정 완전 초기화 (localStorage 포함)
export const resetAllTableSettings = (): void => {
  // 메모리 초기화
  resetAllTables();
  
  // localStorage 초기화
  try {
    localStorage.removeItem('table-settings');
    console.log('테이블 설정이 localStorage에서도 초기화되었습니다.');
  } catch (error) {
    console.error('localStorage 초기화 실패:', error);
  }
};

// 학생 목록 관리
let currentStudents: Student[] = [...STUDENTS];

// 학생 목록 가져오기
export const getStudents = (): Student[] => {
  return [...currentStudents];
};

// 학생 목록 업데이트
export const updateStudents = (newStudents: Student[]): void => {
  currentStudents = [...newStudents];
};

// 학생 추가
export const addStudent = (name: string): Student => {
  const newStudent: Student = {
    id: `student-${Date.now()}`,
    name,
    parkingNumber: '',
    hasParkingSpace: false,
    registrationDate: new Date().toISOString().split('T')[0],
    isRegistered: false,
    assignedTable: undefined,
  };
  currentStudents.push(newStudent);
  return newStudent;
};

// 학생 제거
export const removeStudent = (studentId: string): boolean => {
  const index = currentStudents.findIndex(s => s.id === studentId);
  if (index !== -1) {
    currentStudents.splice(index, 1);
    return true;
  }
  return false;
};

// 학생 이름 업데이트
export const updateStudentName = (studentId: string, newName: string): boolean => {
  const student = currentStudents.find(s => s.id === studentId);
  if (student) {
    student.name = newName;
    return true;
  }
  return false;
}; 

// 테이블 배정 확률 계산
export const getTableAssignmentProbabilities = (method: 'random' | 'balanced' | 'roundRobin' = 'random'): Array<{tableNumber: number, probability: number, availableSeats: number}> => {
  const availableTables = TABLES.filter(table => table.currentOccupancy < table.capacity);
  
  if (availableTables.length === 0) {
    return [];
  }
  
  switch (method) {
    case 'balanced':
      // 균형 배정: 가장 빈 자리가 많은 테이블이 100% 확률
      const maxAvailable = Math.max(...availableTables.map(t => t.capacity - t.currentOccupancy));
      const bestTables = availableTables.filter(t => (t.capacity - t.currentOccupancy) === maxAvailable);
      return bestTables.map(table => ({
        tableNumber: table.tableNumber,
        probability: 100 / bestTables.length,
        availableSeats: table.capacity - table.currentOccupancy
      }));
      
    case 'roundRobin':
      // 순환 배정: 가장 적은 사람이 있는 테이블이 100% 확률
      const minOccupancy = Math.min(...availableTables.map(t => t.currentOccupancy));
      const leastOccupiedTables = availableTables.filter(t => t.currentOccupancy === minOccupancy);
      return leastOccupiedTables.map(table => ({
        tableNumber: table.tableNumber,
        probability: 100 / leastOccupiedTables.length,
        availableSeats: table.capacity - table.currentOccupancy
      }));
      
    case 'random':
    default:
      // 가중치 랜덤: 빈 자리 제곱에 비례한 확률
      const weightedTables = availableTables.map(table => {
        const availableSeats = table.capacity - table.currentOccupancy;
        const weight = Math.pow(availableSeats, 2);
        return { ...table, weight, availableSeats };
      });
      
      const totalWeight = weightedTables.reduce((sum, table) => sum + table.weight, 0);
      
      return weightedTables.map(table => ({
        tableNumber: table.tableNumber,
        probability: (table.weight / totalWeight) * 100,
        availableSeats: table.availableSeats
      }));
  }
}; 

// 출석 기록에서 테이블 점유율 정확히 계산
export const calculateTableOccupancyFromAttendance = (attendanceRecords: any[]): void => {
  console.log('=== 테이블 점유율 계산 시작 ===');
  console.log('입력된 출석 기록:', attendanceRecords);
  
  // 모든 테이블 점유율 초기화
  TABLES.forEach(table => {
    table.currentOccupancy = 0;
  });
  
  // 출석 기록에서 테이블 배정 정보를 기반으로 점유율 계산
  attendanceRecords.forEach(record => {
    console.log(`처리 중인 기록:`, record);
    if (record.assignedTable) {
      const table = TABLES.find(t => t.tableNumber === record.assignedTable);
      if (table && table.currentOccupancy < table.capacity) {
        table.currentOccupancy++;
        console.log(`✅ 테이블 ${record.assignedTable} 점유율 증가: ${table.currentOccupancy}`);
      } else {
        console.warn(`⚠️ 테이블 ${record.assignedTable} 처리 실패:`, {
          tableFound: !!table,
          currentOccupancy: table?.currentOccupancy,
          capacity: table?.capacity
        });
      }
    } else {
      console.log(`❌ 테이블 배정 정보 없음: ${record.studentName}`);
    }
  });
  
  console.log('최종 테이블 현황:', TABLES.map(t => `${t.tableNumber}번: ${t.currentOccupancy}/${t.capacity}`));
  console.log('=== 테이블 점유율 계산 완료 ===');
};

// 테이블 점유율 동기화 (출석 기록과 일치시키기)
export const syncTableOccupancy = (attendanceRecords: any[]): void => {
  calculateTableOccupancyFromAttendance(attendanceRecords);
}; 

// 현재 출석 인원 수를 가져오는 함수
export const getCurrentAttendanceCount = (): number => {
  return TABLES.reduce((total, table) => total + table.currentOccupancy, 0);
};

// 새로운 테이블 배정 규칙 (1-14번째는 1-7번 테이블에 골고루 배정)
export const assignTableWithNewRule = (method: 'random' | 'balanced' | 'roundRobin' = 'random'): number => {
  const currentCount = getCurrentAttendanceCount();
  
  // 1-7번째 사람: 1-7번 테이블에 랜덤 배정
  if (currentCount < 7) {
    const availableTables = TABLES.slice(0, 7).filter(table => table.currentOccupancy === 0);
    if (availableTables.length === 0) {
      throw new Error('1-7번 테이블이 모두 만석입니다.');
    }
    const randomIndex = Math.floor(Math.random() * availableTables.length);
    return availableTables[randomIndex].tableNumber;
  }
  
  // 8-14번째 사람: 1-7번 테이블에 랜덤 배정 (이미 앉은 테이블 제외)
  if (currentCount < 14) {
    const availableTables = TABLES.slice(0, 7).filter(table => table.currentOccupancy < 2);
    if (availableTables.length === 0) {
      throw new Error('1-7번 테이블이 모두 2명씩 찼습니다.');
    }
    const randomIndex = Math.floor(Math.random() * availableTables.length);
    return availableTables[randomIndex].tableNumber;
  }
  
  // 15번째 사람부터: 기존 규칙 적용
  switch (method) {
    case 'balanced':
      return assignBalancedTable();
    case 'roundRobin':
      return assignRoundRobinTable();
    case 'random':
    default:
      return assignRandomTable();
  }
}; 